import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PassportRecord } from './passport.service';
import { PassportDetailsStore } from './passport-details.store';
import {
  DesktopTabStatusRowComponent,
  DesktopStatusRowComponent,
  FormattedDateComponent,
  ActionButtonsComponent,
  DesktopPaginationComponent
} from './desktop-table-templates.component';

/**
 * Shared desktop table component for displaying passports in a tabular format.
 * Used by both Gatehouse and Scheduler components to eliminate code duplication.
 */
@Component({
  selector: 'app-passports-desktop-table',
  standalone: true,
  imports: [
    CommonModule,
    DesktopTabStatusRowComponent,
    DesktopStatusRowComponent,
    FormattedDateComponent,
    ActionButtonsComponent,
    DesktopPaginationComponent
  ],
  template: `
    <div class="desktop-view">
      <ng-container *ngIf="sortedPaginatedRecords.length > 0; else emptyState">
        <div class="desktop-main-container">
          <!-- Status Tabs Row -->
          <app-desktop-tab-status-row *ngIf="!hideStatusTabs"
            [selectedTab]="selectedTab"
            [allCount]="allCount"
            [loadCount]="loadCount"
            [shuntingCount]="shuntingCount"
            [transloadingCount]="transloadingCount"
            [receivingCount]="receivingCount"
            [transferredCount]="transferredCount"
            [itemsPerPage]="itemsPerPage"
            [rowsDropdownOpen]="rowsDropdownOpen"
            (tabSelect)="tabSelect.emit($any($event))"
            (toggleRowsDropdown)="toggleRowsDropdown.emit($event)"
            (setItemsPerPage)="setItemsPerPage.emit($event)"
            (refresh)="refresh.emit()">
          </app-desktop-tab-status-row>

          <div class="desktop-status-row" *ngIf="hideStatusTabs">
            <app-desktop-status-row
              [itemsPerPage]="itemsPerPage"
              [rowsDropdownOpen]="rowsDropdownOpen"
              (toggleRowsDropdown)="toggleRowsDropdown.emit($event)"
              (setItemsPerPage)="setItemsPerPage.emit($event)"
              (refresh)="refresh.emit()">
            </app-desktop-status-row>
          </div>

          <!-- Table Container -->
          <div class="desktop-table-container">
            <table class="desktop-passports-table">
              <thead>
                <tr>
                  <th (click)="sort.emit('loadNumber')" (keydown.enter)="sort.emit('loadNumber')" (keydown.space)="sort.emit('loadNumber'); $event.preventDefault()" tabindex="0">Load Number <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('scheduled')" (keydown.enter)="sort.emit('scheduled')" (keydown.space)="sort.emit('scheduled'); $event.preventDefault()" tabindex="0">Scheduled <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('vendorCombined')" (keydown.enter)="sort.emit('vendorCombined')" (keydown.space)="sort.emit('vendorCombined'); $event.preventDefault()" tabindex="0">Vendor Number/Name <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('variety')" (keydown.enter)="sort.emit('variety')" (keydown.space)="sort.emit('variety'); $event.preventDefault()" tabindex="0">Variety <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('origin')" (keydown.enter)="sort.emit('origin')" (keydown.space)="sort.emit('origin'); $event.preventDefault()" tabindex="0">Origin <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('destination')" (keydown.enter)="sort.emit('destination')" (keydown.space)="sort.emit('destination'); $event.preventDefault()" tabindex="0">Destination <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('haulier')" (keydown.enter)="sort.emit('haulier')" (keydown.space)="sort.emit('haulier'); $event.preventDefault()" tabindex="0">Haulier <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('apnName')" (keydown.enter)="sort.emit('apnName')" (keydown.space)="sort.emit('apnName'); $event.preventDefault()" tabindex="0">APN/Name <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('bulkerNumber')" (keydown.enter)="sort.emit('bulkerNumber')" (keydown.space)="sort.emit('bulkerNumber'); $event.preventDefault()" tabindex="0">Bulker Number <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th (click)="sort.emit('passportComplete')" (keydown.enter)="sort.emit('passportComplete')" (keydown.space)="sort.emit('passportComplete'); $event.preventDefault()" tabindex="0">Passport Complete <img src="/assets/images/Sort.png" class="sort-icon" alt="sort"></th>
                  <th class="col-actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let rec of sortedPaginatedRecords; trackBy: trackById">
                  <ng-container *ngIf="detailsStore.get(rec.id) as details">
                    <tr [attr.data-passport-id]="rec.id"
                        [class.has-pcr-changes]="details?.hasSeenPcrChanges === false && (details?.loadStatusName || '').toLowerCase() !== 'cancelled'"
                        [class.is-cancelled]="(details?.loadStatusName || '').toLowerCase() === 'cancelled'">
                      <td class="col-load-number" 
                          (click)="(details?.loadStatusName || '').toLowerCase() !== 'cancelled' && openPassportDetails.emit(rec.id)" 
                          (keydown.enter)="(details?.loadStatusName || '').toLowerCase() !== 'cancelled' && openPassportDetails.emit(rec.id)"
                          (keydown.space)="(details?.loadStatusName || '').toLowerCase() !== 'cancelled' && openPassportDetails.emit(rec.id); $event.preventDefault()"
                          tabindex="0"
                          [style.cursor]="(details?.loadStatusName || '').toLowerCase() === 'cancelled' ? 'not-allowed' : 'pointer'"
                          [style.text-decoration]="(details?.loadStatusName || '').toLowerCase() === 'cancelled' ? 'none' : 'underline'">
                        {{ details?.loadNumber || rec.id }}
                      </td>
                      <td>
                        <app-formatted-date [dueDateTime]="details?.dueDateTime"></app-formatted-date>
                      </td>
                      <td>{{ (details?.vendorNumber || '') + ' - ' + (details?.vendorName || '') }}</td>
                      <td>{{ details?.variety || '-' }}</td>
                      <td>{{ details?.origin || 'No Origin Field' }}</td>
                      <td>{{ details?.destinationWeighbridge || details?.destination || '-' }}</td>
                      <td>{{ details?.haulier || '-' }}</td>
                      <td>{{ details?.apnName ? (details?.apn + ' - ' + details?.apnName) : (details?.apn || '-') }}</td>
                      <td>{{ details?.bulkerMain || details?.bulkerNumber || '-' }}</td>
                      <td>{{ details?.isPassportCompleted ? 'Yes' : 'No' }}</td>
                      <app-action-buttons 
                        [recordId]="rec.id"
                        (history)="openHistory.emit($event)"
                        (comments)="openComments.emit($event)">
                      </app-action-buttons>
                    </tr>
                  </ng-container>
                </ng-container>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <app-desktop-pagination 
            [currentPage]="currentPage"
            [itemsPerPage]="itemsPerPage"
            [totalPages]="totalPages"
            [visibleRecordsLength]="visibleRecordsLength"
            (pageChange)="pageChange.emit($event)">
          </app-desktop-pagination>
        </div>
      </ng-container>

      <ng-template #emptyState>
        <div class="desktop-empty-container">
          <div class="desktop-main-container empty-state-header" *ngIf="!hideStatusTabs">
            <app-desktop-tab-status-row
              [selectedTab]="selectedTab"
              [allCount]="allCount"
              [loadCount]="loadCount"
              [shuntingCount]="shuntingCount"
              [transloadingCount]="transloadingCount"
              [receivingCount]="receivingCount"
              [transferredCount]="transferredCount"
              [itemsPerPage]="itemsPerPage"
              [rowsDropdownOpen]="rowsDropdownOpen"
              (tabSelect)="tabSelect.emit($any($event))"
              (toggleRowsDropdown)="toggleRowsDropdown.emit($event)"
              (setItemsPerPage)="setItemsPerPage.emit($event)"
              (refresh)="refresh.emit()">
            </app-desktop-tab-status-row>
          </div>

          <div class="desktop-main-container empty-state-header" *ngIf="hideStatusTabs">
            <div class="desktop-status-row">
              <app-desktop-status-row
                [itemsPerPage]="itemsPerPage"
                [rowsDropdownOpen]="rowsDropdownOpen"
                (toggleRowsDropdown)="toggleRowsDropdown.emit($event)"
                (setItemsPerPage)="setItemsPerPage.emit($event)"
                (refresh)="refresh.emit()">
              </app-desktop-status-row>
            </div>
          </div>

          <div class="desktop-empty-state">
            <ng-container *ngIf="customEmptyText; else defaultEmpty">
              <div class="empty-text custom-empty-text">{{ customEmptyText }}</div>
            </ng-container>
            <ng-template #defaultEmpty>
              <div class="empty-circle">
                <img src="/assets/images/No_Passports_Icon.png" class="empty-icon" alt="No passports">
              </div>
              <div class="empty-text">No Passports Yet</div>
            </ng-template>
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styleUrl: './passports-desktop-table.component.css'
})
export class PassportsDesktopTableComponent {
  // Data inputs
  @Input() sortedPaginatedRecords: PassportRecord[] = [];
  @Input() visibleRecordsLength: number = 0;
  @Input() detailsStore!: PassportDetailsStore;

  // Empty state customisation
  @Input() customEmptyText: string = '';

  // Tab inputs
  @Input() hideStatusTabs: boolean = false;
  @Input() selectedTab: string = 'all';
  @Input() allCount: number = 0;
  @Input() loadCount: number = 0;
  @Input() shuntingCount: number = 0;
  @Input() transloadingCount: number = 0;
  @Input() receivingCount: number = 0;
  @Input() transferredCount: number = 0;

  // Pagination inputs
  @Input() itemsPerPage: number = 10;
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() rowsDropdownOpen: boolean = false;

  // Tab outputs
  @Output() tabSelect = new EventEmitter<'all' | 'load' | 'shunting' | 'transloading' | 'receiving' | 'transferred'>();

  // Table outputs
  @Output() sort = new EventEmitter<string>();
  @Output() openPassportDetails = new EventEmitter<string>();
  @Output() openHistory = new EventEmitter<string>();
  @Output() openComments = new EventEmitter<string>();

  // Pagination outputs
  @Output() pageChange = new EventEmitter<number>();
  @Output() toggleRowsDropdown = new EventEmitter<Event>();
  @Output() setItemsPerPage = new EventEmitter<number>();
  @Output() refresh = new EventEmitter<void>();

  trackById(index: number, item: PassportRecord): string {
    return item.id;
  }
}
